import { AdditionalFieldEvent } from "@/types/AdditionalFieldEvent";
import {
  AppConfig,
  Event,
  EventDetailLayouts,
  GuestDetailLayout,
  NamedGuestDetailLayout,
} from "@/types/Event";
import { handleError } from "@/lib/api/errors/ErrorHandler";
import {
  apiDelete,
  apiPut,
  apiGet,
  apiPost,
  apiPostFormData,
} from "../_apiShim";
import { EventGuestQuota } from "@/types/EventGuestQuota";
import { EventEntitlements } from "@/types/EventEntitlements";
import { EventSmsNumber } from "@/types/EventSmsNumber";
import { EventRowDTO } from "@/types/EventRowDTO";
import { EventRoleCreationDTO } from "@/types/EventRoleCreationDTO";
import { Filter } from "@/types/Filter";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";

const BASE_PATH = "/api/v1/events/";

export interface CreateEventResponse {
  eventId: string;
  collaboratorsInvited: number;
  imageUploaded: boolean;
  /** Template-apply guest seeds (best-effort, null when not requested). */
  crmImport?: GuestSeedOutcome | null;
  guestClone?: GuestSeedOutcome | null;
  /**
   * What the clone silently changed to make it fit this event: computed
   * formulas that no longer resolve here, and calculated automation rows whose
   * operand is unavailable or IT-04 protected here.
   *
   * Optional because the duplicate lane still answers a bare hex string when
   * nothing was copied — see the normaliser below.
   */
  droppedOnClone?: string[];
}

/** Best-effort guest-seed outcome (template-apply CRM import / guest clone).
 *  The event itself was created; errorCode reports a partial/failed seed. */
export interface GuestSeedOutcome {
  requested: number;
  created: number;
  skipped: number;
  truncated: boolean;
  droppedFieldNames: string[];
  errorCode: "eventAtCapacity" | "contingentExceeded" | "internal" | null;
}

export async function changeBlockedCountForEvent(
  eventId: string,
  companyId: string,
  blockedCount: number,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${companyId}/${eventId}/change-blocked-count`,
    { blockedCount },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Create an event — single multipart request that handles event creation,
// optional collaborator invites, and optional title image in one atomic
// call. Backend endpoint consumes MULTIPART_FORM_DATA with:
//   - `body` (application/json): the event payload + collaborators list
//   - `image` (file, optional): title image (JPEG/PNG, ≤ 2.5 MB)
// Any failure rolls back the event + roles + S3 + quota.
export async function createEvent(
  event: Event,
  companyId: string,
  collaborators: EventRoleCreationDTO[] = [],
  image?: { buffer: ArrayBuffer; fileName: string; mimeType: string },
  /**
   * Art. 50 Abs. 2 KI-VO: the AI lane that wrote wizard values into this
   * event (`apply_event_wizard_state`). Query parameter only — the multipart
   * body is read by a strict `ObjectMapper` that refuses unknown fields. The
   * server stamps the new event's guest-content mark after the insert.
   */
  aiContentTool?: string,
): Promise<CreateEventResponse> {
  const formData = new FormData();
  const bodyPayload = {
    name: event.name,
    start: event.start!.toISOString(),
    end: event.end!.toISOString(),
    timeZone: event.timeZone,
    registrationType: event.registrationType,
    address: event.address,
    address2: event.address2,
    zipCode: event.zipCode,
    city: event.city,
    country: event.country,
    locationName: event.locationName,
    description: event.description,
    expectedCapacity: event.expectedCapacity,
    onlineUrl: event.onlineUrl,
    additionalFields: event.additionalFields,
    plugins: event.plugins,
    collaborators,
  };
  formData.append(
    "body",
    new Blob([JSON.stringify(bodyPayload)], { type: "application/json" }),
  );
  if (image) {
    formData.append(
      "image",
      new Blob([image.buffer], { type: image.mimeType }),
      image.fileName,
    );
  }

  return await apiPostFormData<CreateEventResponse>(
    withAiContentTool(`${BASE_PATH}${companyId}`, aiContentTool),
    formData,
    {
      "Cache-Control": "no-store",
    },
  );
}


// Get events by company ID
export async function getEventsByCompanyId(
  companyId: string,
  fields: string,
): Promise<Event[]> {
  try {
    return await apiGet<Event[]>(
      `${BASE_PATH}${companyId}/by-company?fields=${fields}`,
      {
        "Cache-Control": "no-store",
      },
      true,
    );
  } catch (error) {
    return handleError(error);
  }
}

// Get an event by ID
export async function getEvent(
  eventId: string,
  fields: string,
): Promise<Event> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  return await apiGet<Event>(
    `${BASE_PATH}${eventId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Guest contingent for one event, resolved server-side against the company
// that OWNS the event. Deliberately NOT getCompany(): that endpoint is pinned
// to the caller's own company, which on a foreign event governs nothing.
export async function getEventGuestQuota(
  eventId: string,
): Promise<EventGuestQuota> {
  return await apiGet<EventGuestQuota>(
    `${BASE_PATH}${eventId}/guest-quota`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Plan features, mail quota, AI credits, company name and the caller's role —
// all for the company that OWNS the event. Same choice as guest-quota: NOT
// getCompany() / fetchSubscriptions(), which are pinned to the login company
// and refuse a same-company EDITOR / VIEWER outright.
export async function getEventEntitlements(
  eventId: string,
): Promise<EventEntitlements> {
  return await apiGet<EventEntitlements>(
    `${BASE_PATH}${eventId}/entitlements`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// The verified SMS numbers of the company that OWNS the event, masked
// (`GET /api/v1/events/{eventId}/sms-numbers`, WORKFLOWS_READ). The SEND_SMS
// editor's fixed-recipient picker; never the plain numbers.
export async function getEventSmsNumbers(eventId: string): Promise<EventSmsNumber[]> {
  return await apiGet<EventSmsNumber[]>(
    `${BASE_PATH}${eventId}/sms-numbers`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Get all events
export async function getEvents(
  fields: string,
  searchTerm: string,
  page: number,
  size: number,
  filter: Filter[],
  sortBy: string = "createdAt",
  sortOrder: "asc" | "desc" = "desc",
): Promise<EventRowDTO[]> {
  const queryParams = `?fields=${encodeURIComponent(
    fields + ",eventId",
  )}&searchTerm=${encodeURIComponent(searchTerm)}&page=${encodeURIComponent(
    page,
  )}&size=${encodeURIComponent(size)}&filters=${encodeURIComponent(
    JSON.stringify(filter),
  )}&sortBy=${encodeURIComponent(sortBy)}&sortOrder=${encodeURIComponent(
    sortOrder,
  )}`;

  const response = await apiGet<EventRowDTO[]>(
    `${BASE_PATH}${queryParams.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  if (!Array.isArray(response)) {
    throw new Error("unknown");
  }

  return response;
}

// Archive/unarchive an event
export async function archiveEvent(
  eventId: string,
  archive: boolean,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/archive?archive=${archive}`,
    {},
    {
      "Cache-Control": "no-store",
    },
  );
}

// Update an event
export async function updateEvent(
  eventId: string,
  event: EventRowDTO,
  ifMatch?: string,
): Promise<void> {
  try {
    const body: Record<string, unknown> = {};

    if (event.name !== undefined) body.name = event.name;
    if (event.start !== undefined) body.start = event.start.toISOString();
    if (event.end !== undefined) body.end = event.end.toISOString();
    if (event.timeZone !== undefined) body.timeZone = event.timeZone;
    if (event.registrationType !== undefined) body.registrationType = event.registrationType;
    if (event.address !== undefined) body.address = event.address;
    if (event.address2 !== undefined) body.address2 = event.address2;
    if (event.zipCode !== undefined) body.zipCode = event.zipCode;
    if (event.city !== undefined) body.city = event.city;
    if (event.country !== undefined) body.country = event.country;
    if (event.locationName !== undefined) body.locationName = event.locationName;
    if (event.additionalFields !== undefined) body.additionalFields = event.additionalFields;
    if (event.fieldDisplayColors !== undefined) body.fieldDisplayColors = event.fieldDisplayColors;
    if (event.fieldConditions !== undefined) body.fieldConditions = event.fieldConditions;
    if (event.fieldRequired !== undefined) body.fieldRequired = event.fieldRequired;
    if (event.guestDetailLayout !== undefined) body.guestDetailLayout = event.guestDetailLayout;
    if (event.archived !== undefined) body.archived = event.archived;
    if (event.plugins !== undefined) body.plugins = event.plugins;
    if (event.sidebarOrder !== undefined) body.sidebarOrder = event.sidebarOrder;
    if (event.defaultRegistrationMode !== undefined) body.defaultRegistrationMode = event.defaultRegistrationMode;
    if (event.globalGuestLimit !== undefined) body.globalGuestLimit = event.globalGuestLimit;
    if (event.globalGuestLimitEnabled !== undefined) body.globalGuestLimitEnabled = event.globalGuestLimitEnabled;
    if (event.globalCountingStatuses !== undefined) body.globalCountingStatuses = event.globalCountingStatuses;
    if (event.globalCountingConditions !== undefined) body.globalCountingConditions = event.globalCountingConditions;
    if (event.globalWaitlistEnabled !== undefined) body.globalWaitlistEnabled = event.globalWaitlistEnabled;
    if (event.globalWaitlistLimit !== undefined) body.globalWaitlistLimit = event.globalWaitlistLimit;
    if (event.defaultRegistrationConditions !== undefined) body.defaultRegistrationConditions = event.defaultRegistrationConditions;
    if (event.defaultRegistrationMessage !== undefined) body.defaultRegistrationMessage = event.defaultRegistrationMessage;
    if (event.showVeltricBranding !== undefined) body.showVeltricBranding = event.showVeltricBranding;
    if (event.crmFeedEnabled !== undefined) body.crmFeedEnabled = event.crmFeedEnabled;
    // Companion door policies. The walk-up pair was absent here since it
    // shipped: the Rules card put it in the payload, this allowlist dropped
    // it, and the card then rebaselined its "saved" snapshot — a save that
    // reported success and changed nothing.
    if (event.walkUpCompanionLimitEnabled !== undefined)
      body.walkUpCompanionLimitEnabled = event.walkUpCompanionLimitEnabled;
    if (event.maxWalkUpCompanionsPerGuest !== undefined)
      body.maxWalkUpCompanionsPerGuest = event.maxWalkUpCompanionsPerGuest;
    if (event.walkUpCompanionsIgnoreGlobalLimit !== undefined)
      body.walkUpCompanionsIgnoreGlobalLimit = event.walkUpCompanionsIgnoreGlobalLimit;
    if (event.websiteCompanionLimitEnabled !== undefined)
      body.websiteCompanionLimitEnabled = event.websiteCompanionLimitEnabled;
    if (event.maxWebsiteCompanionsPerGuest !== undefined)
      body.maxWebsiteCompanionsPerGuest = event.maxWebsiteCompanionsPerGuest;
    if (event.websiteCompanionRules !== undefined)
      body.websiteCompanionRules = event.websiteCompanionRules;
    if (event.companionRegistrationMode !== undefined)
      body.companionRegistrationMode = event.companionRegistrationMode;
    if (event.registrationQuotas !== undefined)
      body.registrationQuotas = event.registrationQuotas;
    // Portal-link lifetime. Same trap as the companion policies above: the
    // Ticket section edits it, and without this line the PUT reports success
    // while the server keeps its default.
    if (event.portalTokenTtlDays !== undefined)
      body.portalTokenTtlDays = event.portalTokenTtlDays;

    const headers: Record<string, string> = {
      "Cache-Control": "no-store",
    };
    if (ifMatch) headers["If-Match"] = ifMatch;
    await apiPut<void>(
      `${BASE_PATH}${eventId}`,
      body,
      headers,
    );
  } catch (error) {
    return handleError(error);
  }
}

// F22 narrow endpoints — EVENT-EDITOR-accessible. The fat PUT above is
// ADMIN+ only; these three scoped PUTs cover the operations EDITOR still
// legitimately needs (sidebar reorder, plugin toggle, registration mode).

export async function updateEventSidebarOrder(
  eventId: string,
  sidebarOrder: string[],
): Promise<void> {
  try {
    await apiPut<void>(
      `${BASE_PATH}${eventId}/sidebar-order`,
      { sidebarOrder },
      {
        "Cache-Control": "no-store",
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

export async function updateEventGuestDetailLayout(
  eventId: string,
  guestDetailLayout: GuestDetailLayout,
): Promise<void> {
  try {
    await apiPut<void>(
      `${BASE_PATH}${eventId}/guest-detail-layout`,
      { guestDetailLayout },
      {
        "Cache-Control": "no-store",
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

/**
 * Whole-array replace of the NAMED guest-detail layouts. `ifMatch` (the
 * event's `updatedAt` ISO instant) enables CAS — a concurrent edit then 409s
 * with `staleIfMatch` instead of silently destroying another editor's layout.
 */
export async function updateEventGuestDetailLayouts(
  eventId: string,
  guestDetailLayouts: NamedGuestDetailLayout[],
  ifMatch?: string,
): Promise<void> {
  try {
    await apiPut<void>(
      `${BASE_PATH}${eventId}/guest-detail-layouts`,
      { guestDetailLayouts },
      {
        "Cache-Control": "no-store",
        ...(ifMatch ? { "If-Match": ifMatch } : {}),
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

/**
 * MERGE of the per-entity detail-popup config. Only the entities present in
 * `detailLayouts` are written — the backend $sets one dot-path per entity key,
 * so saving the speaker popup's config never clobbers a concurrently edited
 * sponsor one.
 *
 * ⚠ The merge is per ENTITY, not per strip: the entity object is REPLACED
 * wholesale, so a body carrying only `{tabs}` drops that entity's stored
 * `actions` — and, since named layouts live in the same object, its whole
 * `layouts` array. Always send the complete entity object.
 *
 * `ifMatch` (the event's `updatedAt` ISO instant) enables CAS: two builder
 * tabs editing different named layouts then 409 with `staleIfMatch` instead
 * of one silently destroying the other's work.
 */
export async function updateEventDetailLayouts(
  eventId: string,
  detailLayouts: EventDetailLayouts,
  ifMatch?: string,
): Promise<void> {
  try {
    await apiPut<void>(
      `${BASE_PATH}${eventId}/detail-layouts`,
      { detailLayouts },
      {
        "Cache-Control": "no-store",
        ...(ifMatch ? { "If-Match": ifMatch } : {}),
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

export async function updateEventPlugins(
  eventId: string,
  plugins: string[],
): Promise<void> {
  try {
    await apiPut<void>(
      `${BASE_PATH}${eventId}/plugins`,
      { plugins },
      {
        "Cache-Control": "no-store",
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

export async function updateEventAppConfig(
  eventId: string,
  appConfig: AppConfig,
  ifMatch?: string,
  /**
   * Art. 50 Abs. 2 KI-VO (plan K1/K2): the AI lane behind this write
   * (`app-wizard`, `app_from_website`). A write-only field of the backend's
   * own `AppConfigUpdate` DTO, so it rides the body — next to, never inside,
   * the stored config (callers keep it out of their `appConfig` state).
   */
  aiContentTool?: string,
): Promise<void> {
  try {
    // The backend AppConfigUpdate DTO expects the fields FLAT ({ tabs,
    // qrLoginEnabled, theme, homeBlocks, pages }), NOT wrapped in an
    // `appConfig` envelope — send the object directly. (updateEventPlugins can
    // wrap because its DTO field happens to be named `plugins`; there is no
    // `appConfig` DTO field, so a wrapped body would bind to null and silently
    // store {}.) `If-Match` enables optimistic-concurrency: the richer builder
    // + two open editor tabs make lost-updates real, so pass the event's
    // updatedAt ETag to 409 on a stale write.
    await apiPut<void>(
      `${BASE_PATH}${eventId}/app-config`,
      aiContentTool ? { ...appConfig, aiContentTool } : appConfig,
      {
        "Cache-Control": "no-store",
        ...(ifMatch !== undefined ? { "If-Match": ifMatch } : {}),
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

export async function updateEventRegistrationConfig(
  eventId: string,
  config: {
    defaultRegistrationMode?: string;
    defaultRegistrationConditions?: Record<string, unknown>;
    defaultRegistrationMessage?: string;
  },
): Promise<void> {
  try {
    const body: Record<string, unknown> = {};
    if (config.defaultRegistrationMode !== undefined)
      body.defaultRegistrationMode = config.defaultRegistrationMode;
    if (config.defaultRegistrationConditions !== undefined)
      body.defaultRegistrationConditions = config.defaultRegistrationConditions;
    if (config.defaultRegistrationMessage !== undefined)
      body.defaultRegistrationMessage = config.defaultRegistrationMessage;

    await apiPut<void>(
      `${BASE_PATH}${eventId}/registration-config`,
      body,
      {
        "Cache-Control": "no-store",
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

// Delete an event
export async function deleteEvent(
  eventId: string,
): Promise<void> {
  try {
    await apiDelete<void>(`${BASE_PATH}${eventId}`, {
      "Cache-Control": "no-store",
    });
  } catch (error) {
    return handleError(error);
  }
}

/** Why a single row failed. Mirrors `BulkActionResult.REASON_*` on the server. */
export type BulkFailureReason =
  /** The caller may not do this. Row remains. */
  | "insufficientCapability"
  /** A concurrent action got there first, or the row was already gone. */
  | "raceLost"
  /** The row IS gone; a step after the write failed. No retry. */
  | "cascadeFailed"
  /** Any business error code passed through verbatim, e.g. "activeSubscription". */
  | (string & {});

export type BulkFailure = { id: string; reason?: BulkFailureReason };

/**
 * Shape of the backend `BulkActionResult` response body (per-row aggregation).
 * `failures` is additive over `failedIds` and may be absent on older servers —
 * always read it through `normalizeBulkOutcome` in `@/lib/bulk/bulkFailure`.
 */
export type BulkActionResult = {
  processed: number;
  failed: number;
  failedIds: string[];
  failures?: BulkFailure[];
};

/**
 * Filter-aware bulk archive/unarchive. Provide either `eventIds` or `filters`.
 * Backend returns a per-row outcome so the caller can surface partial failures
 * (e.g. CloudFront disable timing out on some events).
 */
export async function archiveEventsBulk(
  body: {
    eventIds?: string[];
    filters?: Filter[];
    excludeIds?: string[];
    search?: string;
    archived?: boolean;
    archive: boolean;
  },
): Promise<BulkActionResult> {
  return await apiPost<BulkActionResult>(
    `${BASE_PATH}batch-archive`,
    body,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteEventsBulk(
  body: {
    eventIds?: string[];
    filters?: Filter[];
    excludeIds?: string[];
    search?: string;
    archived?: boolean;
  },
): Promise<BulkActionResult> {
  return await apiPost<BulkActionResult>(
    `${BASE_PATH}batch-delete`,
    body,
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete key from additional fields
export async function deleteKeyFromAdditionalFields(
  eventId: string,
  keyToRemove: string,
): Promise<void> {
  try {
    await apiPost<void>(
      `${BASE_PATH}${eventId}/delete-key?keyToRemove=${keyToRemove}`,
      {},
      {
        "Cache-Control": "no-store",
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

// Change event company
export async function changeEventCompany(
  companyId: string,
  eventId: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${companyId}/change-event-company`,
    { eventId },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Get valid event names
export async function getTwentyOneValidEventNames(
  recentEventIds: string[],
): Promise<{
  validEvents: Event[];
  validPinnedEvents: Event[];
  validRecentEvents: Event[];
}> {
  const queryParams = recentEventIds.map(encodeURIComponent).join(",");

  const response = await apiGet<{
    validEvents: Event[];
    validPinnedEvents: Event[];
    validRecentEvents: Event[];
  }>(
    `${BASE_PATH}/valid-event-names?recentEventIds=${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  if (!response)
    return { validEvents: [], validPinnedEvents: [], validRecentEvents: [] };

  return response;
}

// Get additional field keys and types by event IDs
export async function getAdditionalFieldKeysAndTypes(): Promise<Record<string, string[]>> {
  return await apiGet<Record<string, string[]>>(
    `${BASE_PATH}additional-fields`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getValidEventCount(
  archived: boolean,
  filters: Filter[],
  searchTerm: string,
): Promise<number> {
  const params = new URLSearchParams({
    archived: archived.toString(),
    filters: JSON.stringify(filters),
  });

  // Only add searchTerm if it's not empty
  if (searchTerm && searchTerm.trim() !== "") {
    params.append("searchTerm", searchTerm);
  }

  const response = await apiGet<number>(
    `${BASE_PATH}valid-event-count?${params.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );

  return response;
}

// Create additional field
export async function createAdditionalField(
  eventId: string,
  fieldKey: string,
  fieldValue: AdditionalFieldEvent,
): Promise<void> {
  try {
    await apiPost<void>(
      `${BASE_PATH}${eventId}/additional-field`,
      {
        fieldKey,
        fieldValue,
      },
      {
        "Cache-Control": "no-store",
      },
    );
  } catch (error) {
    return handleError(error);
  }
}

/** Normalized duplicate response — the wire's legacy bare-hex-string body
 *  (flag absent) is folded into this shape HERE so callers never see the
 *  dual wire format. */
export interface DuplicateEventResponse {
  eventId: string;
  /** Absent on the wire when null — JSON-B omits nulls. */
  guestClone?: GuestSeedOutcome | null;
  /** See `CreateEventResponse.droppedOnClone`. */
  droppedOnClone?: string[];
}

// Duplicate an event. copyGuests=true also copies the guest list (per-event
// state reset, fresh codes). The wire returns a bare hex string without the
// flag (old-FE compat, BE-side) — normalized to the object shape here.
export async function duplicateEvent(
  eventId: string,
  name: string | undefined,
  copyGuests?: boolean,
): Promise<DuplicateEventResponse> {
  const raw = await apiPost<string | DuplicateEventResponse>(
    `${BASE_PATH}${eventId}/duplicate`,
    {
      ...(name ? { name } : {}),
      ...(copyGuests ? { copyGuests: true } : {}),
    },
    {
      "Cache-Control": "no-store",
    },
  );
  // ⚠ Not `typeof raw === "string"`. axios JSON-parses the body, so the legacy
  // bare id does not always arrive as a string: an ObjectId hex that happens to
  // be all digits (~1 in 79 000) parses as a NUMBER. Discriminating on "is it an
  // object" keeps that case out of the object branch, where `eventId` would
  // read `undefined` and the caller would navigate to `/event/undefined`.
  //
  // ⚠ It does NOT recover such an id — `JSON.parse` has already turned it into
  // a float and lost the digits. Nothing at this layer can; the real fix is to
  // stop parsing this response at all (a per-call `transformResponse`), which
  // belongs to the shared client, not here. So it FAILS LOUDLY instead: the
  // duplicate did happen, and a caller that gets a clean error can say so,
  // whereas a silent navigation to a garbage id looks like the copy was lost.
  if (typeof raw === "number") {
    // The one case that is provably unrecoverable, and only that one — a
    // blanket "does it look like an id" check here would also reject bodies
    // this client has no business having an opinion about.
    throw new Error("duplicateEventIdUnreadable");
  }
  return typeof raw === "object" && raw !== null
    ? raw
    : { eventId: String(raw), guestClone: null, droppedOnClone: [] };
}

// Delete additional field
export async function deleteAdditionalField(
  eventId: string,
  fieldKey: string,
): Promise<void> {
  try {
    await apiDelete<void>(
      `${BASE_PATH}${eventId}/additional-field/${encodeURIComponent(fieldKey)}`,
      {
        "Cache-Control": "no-store",
      },
    );
  } catch (error) {
    return handleError(error);
  }
}
